import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { capitalize } from '../helpers/common';
import { formatDateDifference } from '../helpers/date';
import { sortDates, sortStrings } from '../helpers/filter';

import { EDIT_COMPONENT_TYPE } from '../constants/common';
import { MAIN_GROUP_TYPE } from '../constants/group';
import { BLANK_FILTER_TEXT } from '../constants/livestock';
import { TABLE_CONFIG_COLUMNS } from '../constants/table-configurations';

import { COLUMN_SIZE } from '../components/AppTable/constants';

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};
export const useGroupLivestockColumns = ({ filteredInfo, filterOptions }) => {
  return useMemo(() => {
    return [
      {
        ...commonColumnProperties,
        title: 'Livestock ID',
        dataIndex: 'identifier',
        configColumnKey: TABLE_CONFIG_COLUMNS.LIVESTOCK_ID,
        filteredValue: filteredInfo.identifier || null,
        fixed: 'left',
        searchable: true,
        width: COLUMN_SIZE.MD,
        render: (value, record) => <Link to={`/animals/${record.id}`}>{record.identifier}</Link>,
        sorter: (a, b) => sortStrings(a.identifier, b.identifier)
      },
      {
        ...commonColumnProperties,
        title: 'Included',
        dataIndex: 'isIncluded',
        configColumnKey: TABLE_CONFIG_COLUMNS.IS_INCLUDED,
        filteredValue: filteredInfo.isIncluded || null,
        editConfig: {
          type: EDIT_COMPONENT_TYPE.SELECT,
          options: [
            { label: 'Yes', value: 1 },
            { label: 'No', value: 0 }
          ]
        },
        filters: [
          { text: 'Yes', value: true },
          { text: 'No', value: false }
        ],
        width: COLUMN_SIZE.SM,
        onFilter: (value, record) => (value ? record.isIncluded : !record.isIncluded),
        render: value => (value === true ? 'Yes' : 'No'),
        sorter: (a, b) => {
          const strA = a.is_pregnant ? 'Yes' : '';
          const strB = b.is_pregnant ? 'Yes' : '';

          return sortStrings(strA, strB);
        }
      },
      {
        ...commonColumnProperties,
        title: 'Physical Group',
        dataIndex: 'physicalGroup',
        configColumnKey: TABLE_CONFIG_COLUMNS.PHYSICAL_GROUP,
        width: COLUMN_SIZE.SM,
        render: (_, record) => record.labels?.find(label => label.main_type === MAIN_GROUP_TYPE.PHYSICAL)?.name
      },
      {
        ...commonColumnProperties,
        title: 'Mgmt Tag ID',
        dataIndex: 'eartag_management_id',
        configColumnKey: TABLE_CONFIG_COLUMNS.MGMT_TAG_ID,
        filteredValue: filteredInfo.eartag_management_id || null,
        searchable: true,
        width: COLUMN_SIZE.XL,
        sorter: (a, b) => sortStrings(a.eartag_management_id, b.eartag_management_id)
      },
      {
        ...commonColumnProperties,
        title: 'Sex',
        dataIndex: 'sex',
        configColumnKey: TABLE_CONFIG_COLUMNS.SEX,
        filteredValue: filteredInfo.sex || null,
        filters: filterOptions?.sex,
        width: COLUMN_SIZE.LG,
        onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.sex : record?.sex === value),
        render: value => capitalize(value),
        sorter: (a, b) => sortStrings(a.sex, b.sex)
      },
      {
        ...commonColumnProperties,
        title: 'Colour',
        dataIndex: 'colour',
        configColumnKey: TABLE_CONFIG_COLUMNS.COLOUR,
        filteredValue: filteredInfo.colour || null,
        searchable: true,
        width: COLUMN_SIZE.SM,
        sorter: (a, b) => sortStrings(a.colour, b.colour)
      },
      {
        ...commonColumnProperties,
        title: 'Breed',
        dataIndex: 'breedName',
        configColumnKey: TABLE_CONFIG_COLUMNS.BREED,
        width: COLUMN_SIZE.XL,
        filterSearch: true,
        filters: filterOptions?.breeds,
        filteredValue: filteredInfo.breedName || null,
        onFilter: (value, record) =>
          value === BLANK_FILTER_TEXT ? !record?.breed?.display_name : record?.breed?.display_name === value,
        sorter: (a, b) => sortStrings(a.breed?.display_name, b.breed?.display_name),
        render: (value, record) => record?.breed?.display_name
      },
      {
        ...commonColumnProperties,
        title: 'Kraal Tag ID',
        dataIndex: 'tagIdentifier',
        configColumnKey: TABLE_CONFIG_COLUMNS.KRAAL_TAG_ID,
        width: COLUMN_SIZE.LG,
        searchable: true,
        filteredValue: filteredInfo.tagIdentifier || null,
        onFilter: (value, record) => {
          const searchField = record.tag?.diagri_id || 'Unlinked';

          return searchField.toString().toLowerCase().includes(value.toLowerCase());
        },
        render: (value, record) =>
          record?.tag?.id ? <Link to={`/tag/${record?.tag?.id}`}>{record?.tag?.diagri_id}</Link> : 'Unlinked',
        sorter: (a, b) => sortStrings(a.tag?.diagri_id, b.tag?.diagri_id)
      },
      {
        ...commonColumnProperties,
        title: 'Brand',
        dataIndex: 'brand',
        configColumnKey: TABLE_CONFIG_COLUMNS.BRAND,
        searchable: true,
        width: COLUMN_SIZE.MD,
        filteredValue: filteredInfo.brand || null,
        sorter: (a, b) => sortStrings(a.brand, b.brand)
      },
      {
        ...commonColumnProperties,
        title: 'Assigned Pasture',
        dataIndex: 'assignedGeofence',
        configColumnKey: TABLE_CONFIG_COLUMNS.ASSIGNED_GEOFENCE,
        editable: true,
        width: COLUMN_SIZE.XL,
        filterSearch: true,
        filters: filterOptions?.geofences,
        filteredValue: filteredInfo.assignedGeofence || null,
        onFilter: (value, record) =>
          value === BLANK_FILTER_TEXT
            ? !record?.assigned_geofences.length
            : record?.assigned_geofences?.includes(value),
        render: (value, record) => {
          const currentGeofence = record.geofences?.find(g => !g?.is_master);
          return currentGeofence?.name;
        },
        sorter: (a, b) => sortStrings(a.assigned_geofences.at(0), b.assigned_geofences.at(0))
      },
      {
        ...commonColumnProperties,
        title: 'Livestock Type',
        dataIndex: 'stock_type',
        configColumnKey: TABLE_CONFIG_COLUMNS.LIVESTOCK_TYPE,
        editable: true,
        width: COLUMN_SIZE.XL,
        filterSearch: true,
        filters: filterOptions?.types,
        filteredValue: filteredInfo.stock_type || null,
        onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.stock_type : record?.stock_type === value),
        render: value => capitalize(value),
        sorter: (a, b) => sortStrings(a.stock_type, b.stock_type)
      },
      {
        ...commonColumnProperties,
        title: 'Age',
        dataIndex: 'age',
        configColumnKey: TABLE_CONFIG_COLUMNS.AGE,
        width: COLUMN_SIZE.MD,
        searchable: true,
        filteredValue: filteredInfo.age || null,
        onFilter: (value, record) => {
          const age = formatDateDifference(record.dob_at);

          return age.includes(value.toLowerCase());
        },
        render: (value, record) => formatDateDifference(record.dob_at),
        sorter: (a, b) => sortDates(a.dob_at, b.dob_at)
      },
      {
        ...commonColumnProperties,
        title: 'Breeding Status',
        dataIndex: 'breeding_status',
        configColumnKey: TABLE_CONFIG_COLUMNS.BREEDING_STATUS,
        width: COLUMN_SIZE.XL,
        filterSearch: true,
        filters: filterOptions?.breedingStatuses,
        filteredValue: filteredInfo.breeding_status || null,
        onFilter: (value, record) =>
          value === BLANK_FILTER_TEXT ? !record?.breeding_status : record?.breeding_status === value,
        sorter: (a, b) => sortStrings(a.breeding_status, b.breeding_status)
      }
    ];
  }, [filteredInfo, filterOptions]);
};
